import { useCallback } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { triggerExtractPending } from './bankingSlice';
import type { ClickSource } from './constants';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_BANK_EXTRACT_NOW_CLICKED } from '../../utils/constants/mixpanelEvents/investorEvents';

/**
 * Dispatches the bank-statement extraction trigger for a given org.
 *
 * Used by both the dashboard banner ("Extract Now" link) and the empty-state
 * CTA on the Banking tab. Centralises the dispatch contract so the two call
 * sites stay in sync — particularly the orgId guard, which prevents firing
 * the action before the org is resolved from the route. Also centralises the
 * AICA_BANK_EXTRACT_NOW_CLICKED tracking call so every call site emits it
 * exactly once with the correct source.
 *
 * @param orgId - The current org id, or undefined while it's still being
 *   resolved (e.g. on first paint before useParams settles). Calls become
 *   no-ops while undefined.
 * @returns A stable callback `(clickSource) => void` for the consumer.
 */
export const useExtractBankStatements = (orgId: string | undefined) => {
    const dispatch = useAppDispatch();
    return useCallback(
        (clickSource: ClickSource) => {
            if (!orgId) return;
            Mixpanel.track(AICA_BANK_EXTRACT_NOW_CLICKED, { source: clickSource });
            dispatch(triggerExtractPending({ orgId, clickSource }));
        },
        [dispatch, orgId],
    );
};
